/**
 * HistoryAccordionItem Component
 * 단일 그룹의 아코디언 아이템 렌더링 (헤더 + 테이블 + 페이지네이션)
 */
import React from 'react';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { GroupedData } from '../utils/groupHistories';
import { ViewMode } from './HistoryAccordion';
import { formatAmount } from '../utils/amountFormatter';
import { getTransactionLabel, getBadgeClass } from '../utils/withdrawHistory.helpers';

interface HistoryAccordionItemProps {
    group: GroupedData;
    isExpanded: boolean;
    currentPage: number;
    onToggle: (id: string) => void;
    onPageChange: (id: string, page: number) => void;
    viewMode: ViewMode;
    ITEMS_PER_PAGE: number;
}

const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('ko-KR') + '원';
};

const HistoryAccordionItem: React.FC<HistoryAccordionItemProps> = ({
                                                                       group,
                                                                       isExpanded,
                                                                       currentPage,
                                                                       onToggle,
                                                                       onPageChange,
                                                                       viewMode,
                                                                       ITEMS_PER_PAGE,
                                                                   }) => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentItems = group.histories.slice(startIndex, endIndex);
    const totalPages = Math.ceil(group.histories.length / ITEMS_PER_PAGE);

    return (
        <div className="accordion-item">
            {/* 그룹 헤더 */}
            <div
                className="accordion-header"
                onClick={() => onToggle(group.id)}
            >
                <span className="group-name">{group.name}</span>
                <div className="accordion-header-right">
                    <div className="group-info-cards">
                        <div className="info-card">
                            <span className="info-label">현재 잔액</span>
                            <span className="info-value balance">
                                {group.currentBalance !== null
                                    ? formatCurrency(group.currentBalance)
                                    : '-'}
                            </span>
                        </div>
                        <div className="info-card">
                            <span className="info-label">총 입금</span>
                            <span className="info-value deposit">
                                {formatCurrency(group.totalDeposit)}
                            </span>
                        </div>
                        <div className="info-card">
                            <span className="info-label">총 출금</span>
                            <span className="info-value withdrawal">
                                {formatCurrency(group.totalWithdrawal)}
                            </span>
                        </div>
                        {viewMode === 'rider' && (
                            <>
                                <div className="info-card">
                                    <span className="info-label">총 협력사 일차감</span>
                                    <span className="info-value withdrawal">
                                        {formatCurrency(group.totalSdcDeduct)}
                                    </span>
                                </div>
                                <div className="info-card">
                                    <span className="info-label">총 본사 일차감</span>
                                    <span className="info-value withdrawal">
                                        {formatCurrency(group.totalHdcDeduct)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                </div>
            </div>

            {/* 그룹 내용 (테이블) */}
            {isExpanded && (
                <div className="accordion-content">
                    <div className="table-responsive">
                        <table className="history-table">
                            <thead>
                            <tr>
                                <th className="col-no">NO</th>
                                <th className="col-date">처리일자</th>
                                <th className="col-date">정산일자</th>
                                <th className="col-type">구분</th>
                                <th className="col-content">내용</th>
                                {viewMode === 'rider' && (
                                    <th className="col-partner">협력사명</th>
                                )}
                                <th className="col-amount">요청금액</th>
                                <th className="col-amount">잔액</th>
                            </tr>
                            </thead>
                            <tbody>
                            {currentItems.map((history, index) => (
                                <tr key={`${history.no}-${index}`}>
                                    <td className="col-no">
                                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                    </td>
                                    <td className="col-date">{history.processDateTime || '-'}</td>
                                    <td className="col-date">{history.acctDate || '-'}</td>
                                    <td className="col-type">
                                            <span className={`badge ${getBadgeClass(history)}`}>
                                                {getTransactionLabel(history)}
                                            </span>
                                    </td>
                                    <td className="col-content">
                                        {(history.acctType === 'T' || history.acctType === 'TO' || history.acctType === 'TI') && history.briefs
                                            ? history.briefs
                                            : '-'}
                                    </td>
                                    {viewMode === 'rider' && (
                                        <td className="col-partner">{history.suppName || '-'}</td>
                                    )}
                                    <td className={`col-amount ${
                                        history.amount < 0 ? 'amount-withdrawal' : 'amount-deposit'
                                    }`}>
                                        {formatAmount(history.amount)}
                                    </td>
                                    <td className="col-amount">
                                        {history.balAmt !== undefined
                                            ? formatAmount(history.balAmt)
                                            : '-'
                                        }
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 그룹별 페이지네이션 */}
                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page) => onPageChange(group.id, page)}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HistoryAccordionItem;
